import { useTranslate } from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import { useAntTheme } from "hooks/useAntTheme";
import { Spin } from "antd";
import styled from "styled-components";
import { PlusCircleIcon } from "@phosphor-icons/react";
import { MediaProjectListItem } from "../../types";
import { RecentProjectCard } from "./RecentProjectCard";
import { useProjectWizardStore } from "../../projects/useProjectWizardStore";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import { useCreateExperienceEntry } from "hooks/useCreateExperienceEntry";
import { radius } from "@libertify/ui";


export type RecentProjectsProps = {
  projects?: MediaProjectListItem[];
  isLoading: boolean;
  onDeleteSuccess?: () => void | Promise<unknown>;
  isProjectQuotaReached?: boolean;
  showViewAll?: boolean;
};

export const RecentProjects = ({
  projects,
  isLoading,
  onDeleteSuccess,
  isProjectQuotaReached = false,
  showViewAll = true,
}: RecentProjectsProps) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { theme } = useAntTheme();
  const { setWizardStartTime } = useProjectWizardStore();
  const { trackEvent } = useAnalytics();
  const { paywallFlow } = useSoftPaywall();
  const { startCreateExperience, chooserModal } = useCreateExperienceEntry();

  const handleCreateNew = () => {
    if (isProjectQuotaReached) return;
    setWizardStartTime(Date.now());
    trackEvent(AnalyticsEvents.ProjectCreateInit, { page: "home", paywall_flow: paywallFlow });
    startCreateExperience();
  };

  const recentThree = projects?.slice(0, 3);

  return (
    <RecentProjectsSection>
      <RecentProjectsSectionHeader>
        <RecentProjectsSectionTitle>
          {t("pages.home.index.recentProjects")}
        </RecentProjectsSectionTitle>
        {showViewAll && (
          <RecentProjectsViewAllLink
            onClick={() => navigate("/media/projects")}
          >
            {t("pages.home.index.viewAll")} →
          </RecentProjectsViewAllLink>
        )}
      </RecentProjectsSectionHeader>

      <RecentProjectsGrid>
        {isLoading && <Spin size="large" style={{ margin: "40px auto" }} />}

        {!isLoading &&
          recentThree?.map((project) => (
            <RecentProjectCard
              key={project.id}
              project={project}
              onDeleteSuccess={onDeleteSuccess}
            />
          ))}

        {!isLoading && (
          <CreateNewCard
            role="link"
            tabIndex={isProjectQuotaReached ? -1 : 0}
            aria-label={t("pages.home.index.createNew")}
            aria-disabled={isProjectQuotaReached}
            $disabled={isProjectQuotaReached}
            onClick={isProjectQuotaReached ? undefined : handleCreateNew}
            onKeyDown={(e) => {
              if (isProjectQuotaReached) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCreateNew();
              }
            }}
          >
            <PlusCircleIcon size={32} color={"#247F79"} />
            <CreateNewCardLabel $primaryColor={"#247F79"}>
              {t("pages.home.index.createNew")}
            </CreateNewCardLabel>
          </CreateNewCard>
        )}
      </RecentProjectsGrid>

      {chooserModal}
    </RecentProjectsSection>
  );
};

const RecentProjectsSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RecentProjectsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RecentProjectsSectionTitle = styled.h2`
  color: #090e19;
  margin: 0;
  font-family: "Poppins", sans-serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 600;
  line-height: 100%;
  letter-spacing: -1.12px;
`;

const RecentProjectsViewAllLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colorPrimary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const RecentProjectsGrid = styled.div`
  display: grid;
  margin-top: 16px;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (width <= 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (width <= 600px) {
    grid-template-columns: 1fr;
  }
`;

const CreateNewCard = styled.div<{ $disabled?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 210px;
  border-radius: ${radius.box}px;
  background: #6f747f29;
  border: 1px solid #6f747f26;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  transition:
    border-color 0.2s,
    box-shadow 0.2s,
    background-color 0.2s;

  &:hover {
    border-color: ${({ $disabled, theme }) =>
      $disabled ? "rgba(111, 116, 127, 0.15)" : theme.colorPrimary};
    background-color: ${({ $disabled }) =>
      $disabled ? "#6f747f29" : "rgba(255, 255, 255, 0.5)"};
    box-shadow: ${({ $disabled }) =>
      $disabled ? "none" : "0 2px 12px rgba(0, 0, 0, 0.04)"};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colorPrimary};
    outline-offset: 2px;
  }

  @media (width <= 768px) {
    width: 100%;
    max-width: 345px;
  }
`;

const CreateNewCardLabel = styled.span<{ $primaryColor: string }>`
  font-family: "Inter", sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: ${({ $primaryColor }) => $primaryColor};
`;

